import { Dispatch, SetStateAction, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";

import {
  CONDITION_DEFINITIONS,
  CONDITION_OWNERS,
  conditionDefinitionsDescToIdMap,
  conditionOwnersDescToIdMap,
  SetSnackType,
  StudentsNoNulls,
  TeacherConditionsNoNulls,
  TeachersNoNulls,
} from "../../utils";

export const AddTeacherConditionDialog = (props: {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  setSnack: SetSnackType;
  source: StudentsNoNulls;
  targets: TeachersNoNulls[];
  processTchCondAdd: (tchCond: TeacherConditionsNoNulls) => Promise<boolean>;
}) => {
  const defaultDefinition = conditionDefinitionsDescToIdMap.get("Wish") ?? -1;
  const defaultOwner = conditionOwnersDescToIdMap.get("Student") ?? -1;

  const [addCondDef, setAddCondDef] = useState(defaultDefinition);
  const [addTchCondTarget, setAddTchCondTarget] = useState<TeachersNoNulls | null>(null);
  const [addTchCondTargetError, setAddTchCondTargetError] = useState(false);
  const [addCondOwner, setAddCondOwner] = useState(defaultOwner);

  const resetAddCond = () => {
    setAddCondDef(defaultDefinition);
    setAddTchCondTarget(null);
    setAddTchCondTargetError(false);
    setAddCondOwner(defaultOwner);
  };

  const validateAddTeacherCondition = (tchCondToAdd: TeacherConditionsNoNulls) => {
    if (!tchCondToAdd.target_id) {
      setAddTchCondTargetError(true);
      return false;
    }
    return true;
  };

  return (
    <Dialog open={props.open}>
      <DialogTitle>Add Teacher Condition</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Set the new teacher condition below
          <br />
          where the source student is {props.source.last}, {props.source.first}:
        </DialogContentText>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <InputLabel id="add-tchcond-def-label">Definition</InputLabel>
          <Select
            id="add-tchcond-def"
            label="Definition"
            labelId="add-tchcond-label"
            onChange={ev => setAddCondDef(ev.target.value)}
            value={addCondDef}
          >
            {CONDITION_DEFINITIONS.map(d => (
              <MenuItem key={d.id} value={d.id}>
                {d.desc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <InputLabel id="add-tchcond-target-label">Target</InputLabel>
          <Select
            id="add-tchcond-target"
            error={addTchCondTargetError}
            label="Target"
            labelId="add-tchcond-target-label"
            onChange={ev => setAddTchCondTargetError(false)}
            value={addTchCondTarget?.id ?? ""}
          >
            {props.targets.map(t => (
              <MenuItem
                key={t.id}
                value={t.id}
                selected={t.id === addTchCondTarget?.id}
                onClick={() => setAddTchCondTarget(t)}
              >
                {!t ? null : `${(t.prefix ?? "N/A") === "N/A" ? t.first : t.prefix} ${t.last}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl fullWidth required sx={{ my: 1 }}>
          <InputLabel id="add-tchcond-owner-label">Owner</InputLabel>
          <Select
            id="add-tchcond-owner"
            label="Owner"
            labelId="add-tchcond-owner-label"
            onChange={ev => setAddCondOwner(ev.target.value)}
            value={addCondOwner}
          >
            {CONDITION_OWNERS.filter(o => o.desc !== "SYSTEM").map(o => (
              <MenuItem key={o.id} value={o.id}>
                {o.desc}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            props.setOpen(false);
            resetAddCond();
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={async () => {
            const tchCondToAdd = {
              source_id: props.source.id,
              definition_id: addCondDef,
              target_id: addTchCondTarget?.id,
              owner_id: addCondOwner,
            } as TeacherConditionsNoNulls;
            if (!validateAddTeacherCondition(tchCondToAdd)) {
              setAddTchCondTargetError(true);
              props.setSnack("Unable to add teacher condition, check invalid fields", "warning");
              return;
            }
            if (await props.processTchCondAdd(tchCondToAdd)) {
              props.setOpen(false);
              resetAddCond();
            }
          }}
          variant="contained"
        >
          Add Teacher Condition
        </Button>
      </DialogActions>
    </Dialog>
  );
};
